import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { initiativesApi } from '../services/trackerApi';
import { meetingMinutesApi } from '../services/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useToast } from '../hooks/useToast';
import { formatDate } from '../utils/format';
import { PageHeader } from '../components/PageHeader';
import { entryPlainText, todayIso } from './minutes/MinuteEntryRow';

type Bucket = 'pastDue' | 'open' | 'closed';

/** One action assigned to the current user, normalised from either source it can come
 *  from - an initiative's own `actions[]` (direct), or a MinuteEntry with category
 *  'action' (meeting) - into one shape this page can list/sort/mark-done uniformly. */
interface MyAction {
  id: string;
  description: string;
  dueDate?: string;
  bucket: Bucket;
  source: 'direct' | 'meeting';
  initiativeId?: string;
  initiativeName?: string;
  minutesId?: string;
  entryId?: string;
  meetingTitle?: string;
  meetingDate?: string;
}

const bucketOf = (status: string, dueDate: string | undefined, today: string): Bucket => {
  if ((status || '').toLowerCase() === 'done') return 'closed';
  if (dueDate && dueDate < today) return 'pastDue';
  return 'open';
};

const daysBetween = (isoA: string, isoB: string) => Math.round((new Date(isoB).getTime() - new Date(isoA).getTime()) / 86400000);

/** Same three-state due-date treatment InitiativeDetailPage.tsx's own Actions tab already
 *  uses (Overdue in red, a plain date in blue, an em-dash for nothing set) - kept identical
 *  here so "overdue" reads the same way everywhere in the app, not just on this page. */
const DueChip: React.FC<{ item: MyAction }> = ({ item }) => {
  if (item.bucket === 'pastDue') return <span className="np-chip np-tone-danger" style={{ flexShrink: 0 }}>Overdue — {formatDate(item.dueDate!)}</span>;
  if (item.dueDate) return <span className="np-chip np-tone-info" style={{ flexShrink: 0 }}>{formatDate(item.dueDate)}</span>;
  return <span className="np-sub" style={{ fontSize: 12, flexShrink: 0 }}>No due date</span>;
};

const ActionRow: React.FC<{ item: MyAction; onMarkDone?: () => void; busy?: boolean; closed?: boolean }> = ({ item, onMarkDone, busy, closed }) => (
  <div className="d-flex align-items-center gap-3 py-2 border-bottom">
    <span className={`np-icon-badge np-icon-badge-sm np-tone-${item.source === 'direct' ? 'primary' : 'neutral'}`} style={{ opacity: closed ? 0.55 : 1, flexShrink: 0 }}>
      <i className={item.source === 'direct' ? 'ri-flashlight-line' : 'ri-file-list-3-line'}></i>
    </span>
    <div className="flex-grow-1" style={{ minWidth: 0 }}>
      <p
        className="mb-0 text-truncate"
        style={{ fontWeight: 500, textDecoration: closed ? 'line-through' : 'none', color: closed ? 'var(--bs-secondary-color)' : undefined }}
      >
        {item.description}
      </p>
      <p className="np-sub mb-0" style={{ fontSize: 12 }}>
        {item.source === 'direct' ? (
          <Link to={`/tracker/initiatives/${item.initiativeId}`}>{item.initiativeName || 'Untitled initiative'}</Link>
        ) : (
          <Link to={`/meeting-minutes/${item.minutesId}`}>{item.meetingTitle || 'Untitled meeting'} · {formatDate(item.meetingDate || '')}</Link>
        )}
      </p>
    </div>
    {!closed && <DueChip item={item} />}
    {onMarkDone && (
      <button type="button" className="np-btn np-btn-sm np-btn-ghost-success" disabled={busy} onClick={onMarkDone} style={{ flexShrink: 0 }}>
        <i className="ri-check-line"></i> Mark done
      </button>
    )}
  </div>
);

const ActionSection: React.FC<{
  title: string;
  icon: string;
  tone: 'danger' | 'warning';
  items: MyAction[];
  markDone: (item: MyAction) => void;
  busyId?: string;
}> = ({ title, icon, tone, items, markDone, busyId }) => (
  <div className={`np-card mb-3 np-card-accent-${tone}`}>
    <div className="np-card-head d-flex align-items-center gap-2">
      <span className={`np-icon-badge np-icon-badge-sm np-tone-${tone}`}><i className={icon}></i></span>
      <h2 className="mb-0">{title}</h2>
      <span className="np-chip np-tone-neutral ms-auto">{items.length}</span>
    </div>
    <div className="np-card-body pt-2">
      {items.map((item) => (
        <ActionRow key={`${item.source}-${item.id}`} item={item} onMarkDone={() => markDone(item)} busy={busyId === item.id} />
      ))}
    </div>
  </div>
);

export const MyActionsPage: React.FC = () => {
  const { personId } = useCurrentUser();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [closedOpen, setClosedOpen] = useState(false);

  const { data: initiatives = [] } = useQuery({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });
  // Tenant-wide, same query key/shape InitiativeDetailPage.tsx already uses for its own
  // cross-initiative Actions rollup - an action can be linked independently of which
  // initiative (if any) its meeting is pinned to, so this has to look past just the
  // initiatives this person happens to own or follow.
  const { data: allMinutes = [] } = useQuery({ queryKey: ['meetingMinutes', 'all'], queryFn: () => meetingMinutesApi.list() });

  const markDirectActionDone = useMutation({
    mutationFn: ({ initiativeId, actionId }: { initiativeId: string; actionId: string }) => {
      const owner = initiatives.find((i: any) => i.initiativeId === initiativeId);
      const updated = (owner?.actions || []).map((a: any) => (a.id === actionId ? { ...a, status: 'Done' } : a));
      return initiativesApi.update(initiativeId, { actions: updated });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      toast.success('Action marked done.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update action'),
  });

  const markMeetingActionDone = useMutation({
    mutationFn: ({ minutesId, entryId }: { minutesId: string; entryId: string }) => meetingMinutesApi.closeAction(minutesId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      toast.success('Action marked done.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update action'),
  });

  if (!personId) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <PageHeader icon="ri-user-line" title="My Actions" subtitle="Every action assigned to you, across every initiative." />
        <div className="np-card">
          <div className="np-card-body text-center np-sub py-5">
            <i className="ri-user-search-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
            No People-register record matches your account, so actions can't be attributed to you yet.
          </div>
        </div>
      </div>
    );
  }

  const today = todayIso();

  const direct: MyAction[] = initiatives.flatMap((init: any) =>
    (init.actions || [])
      .filter((a: any) => a.ownerId === personId)
      .map((a: any) => ({
        id: a.id,
        description: a.description,
        dueDate: a.dueDate || undefined,
        bucket: bucketOf(a.status, a.dueDate, today),
        source: 'direct' as const,
        initiativeId: init.initiativeId,
        initiativeName: init.name,
      })),
  );

  const fromMeetings: MyAction[] = allMinutes.flatMap((m: any) =>
    (m.entries || [])
      .filter((e: any) => e.category === 'action' && e.ownerId === personId)
      .map((e: any) => ({
        id: e.entryId,
        description: entryPlainText(e.body) || 'No detail recorded',
        dueDate: e.dueDate || undefined,
        bucket: bucketOf(e.status, e.dueDate, today),
        source: 'meeting' as const,
        minutesId: m.minutesId,
        entryId: e.entryId,
        meetingTitle: m.title,
        meetingDate: m.meetingDate,
      })),
  );

  const all = [...direct, ...fromMeetings];
  const byDueDateAsc = (a: MyAction, b: MyAction) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999');
  const pastDue = all.filter((a) => a.bucket === 'pastDue').sort(byDueDateAsc);
  const open = all.filter((a) => a.bucket === 'open').sort(byDueDateAsc);
  const closed = all.filter((a) => a.bucket === 'closed');
  const allClear = pastDue.length === 0 && open.length === 0;

  const oldestOverdueDays = pastDue.length > 0 ? daysBetween(pastDue[0].dueDate!, today) : 0;
  const weekAhead = new Date(new Date(today).getTime() + 7 * 86400000).toISOString().slice(0, 10);
  const dueThisWeekCount = open.filter((a) => a.dueDate && a.dueDate <= weekAhead).length;

  const markDone = (item: MyAction) => {
    if (item.source === 'direct') {
      markDirectActionDone.mutate({ initiativeId: item.initiativeId!, actionId: item.id });
    } else {
      markMeetingActionDone.mutate({ minutesId: item.minutesId!, entryId: item.entryId! });
    }
  };
  const busyId = markDirectActionDone.isPending
    ? markDirectActionDone.variables?.actionId
    : markMeetingActionDone.isPending
      ? markMeetingActionDone.variables?.entryId
      : undefined;

  const scoreCells: { label: string; tone: 'danger' | 'warning' | 'success'; icon: string; value: number; sub?: string }[] = [
    { label: 'Past due', tone: 'danger', icon: 'ri-alarm-warning-line', value: pastDue.length, sub: pastDue.length > 0 ? `oldest is ${oldestOverdueDays} day${oldestOverdueDays === 1 ? '' : 's'} overdue` : undefined },
    { label: 'Open', tone: 'warning', icon: 'ri-time-line', value: open.length, sub: dueThisWeekCount > 0 ? `${dueThisWeekCount} due this week` : undefined },
    { label: 'Closed', tone: 'success', icon: 'ri-checkbox-circle-line', value: closed.length },
  ];

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-user-line"
        title="My Actions"
        subtitle="Every open, past due, and closed action assigned to you, across every initiative and meeting."
      />

      <div className="np-card mb-3">
        <div className="np-card-body p-0">
          <div className="d-flex flex-wrap">
            {scoreCells.map((cell, i) => (
              <div
                key={cell.label}
                className="flex-fill p-3"
                style={{
                  minWidth: 160,
                  borderLeft: `3px solid var(--bs-${cell.tone})`,
                  borderRight: i < scoreCells.length - 1 ? '1px solid var(--bs-border-color)' : undefined,
                }}
              >
                <p
                  className="mb-1 d-flex align-items-center gap-1"
                  style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', color: `var(--bs-${cell.tone})` }}
                >
                  <i className={cell.icon}></i>{cell.label}
                </p>
                <p className="mb-0 np-tabular" style={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--bs-heading-color)' }}>
                  {cell.value}
                </p>
                {cell.sub && <p className="np-sub mb-0" style={{ fontSize: 11 }}>{cell.sub}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {allClear && (
        <div className="np-card mb-3 np-card-accent-success">
          <div className="np-card-body text-center py-4">
            <i className="ri-checkbox-circle-fill d-block mb-2" style={{ fontSize: '1.75rem', color: 'var(--bs-success)' }}></i>
            <p className="mb-0" style={{ fontWeight: 600 }}>You're all caught up</p>
            <p className="np-sub mb-0" style={{ fontSize: 13 }}>Nothing open or past due right now.</p>
          </div>
        </div>
      )}

      {pastDue.length > 0 && (
        <ActionSection title="Past due" icon="ri-alarm-warning-line" tone="danger" items={pastDue} markDone={markDone} busyId={busyId} />
      )}
      {open.length > 0 && (
        <ActionSection title="Open" icon="ri-time-line" tone="warning" items={open} markDone={markDone} busyId={busyId} />
      )}

      <div className="np-accordion-item">
        <button type="button" className="np-accordion-trigger" onClick={() => setClosedOpen((v) => !v)}>
          <span className="d-flex align-items-center gap-2">
            <span className="np-icon-badge np-icon-badge-sm np-tone-success"><i className="ri-checkbox-circle-line"></i></span>
            <strong>Closed</strong>
            <span className="np-chip np-tone-neutral">{closed.length}</span>
          </span>
          <i className={`ri-arrow-${closedOpen ? 'up' : 'down'}-s-line text-muted flex-shrink-0`}></i>
        </button>
        {closedOpen && (
          <div className="np-accordion-body">
            {closed.length === 0 ? (
              <p className="np-sub small mb-0">Nothing closed yet.</p>
            ) : (
              closed.map((item) => <ActionRow key={`${item.source}-${item.id}`} item={item} closed />)
            )}
          </div>
        )}
      </div>
    </div>
  );
};
